'use server'

import { createClient } from '@/lib/supabase/server'
import { revalidatePath } from 'next/cache'
import { sendEmail } from '@/lib/email'
import { WelcomeEmail } from '@/components/emails/welcome-email'
import { StatusUpdateEmail } from '@/components/emails/status-update-email'
import { PaymentReminderEmail } from '@/components/emails/payment-reminder-email'

export async function addNote(formData: FormData) {
    const supabase = await createClient()
    const appId = formData.get('applicationId') as string
    const note = formData.get('note') as string

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    await supabase.from('application_notes').insert({
        application_id: appId,
        author_user_id: user.id,
        note: note,
        is_internal: true
    })

    revalidatePath(`/admin/applications/${appId}`)
}

export async function updateStatus(appId: string, status: string) {
    const supabase = await createClient()
    // Sync both status (legacy) and stage (new) for consistency
    await supabase.from('applications').update({
        status,
        stage: status as any // Assuming status maps to stage labels for now
    }).eq('id', appId)
    revalidatePath(`/admin/applications/${appId}`)
    revalidatePath('/admin/applications')
}

export async function updateAssignee(appId: string, userId: string) {
    const supabase = await createClient()
    await supabase.from('applications').update({
        assigned_to_user_id: userId === '' ? null : userId
    }).eq('id', appId)
    revalidatePath(`/admin/applications/${appId}`)
    revalidatePath('/admin/applications')
}


import { createAdminClient } from '@/lib/supabase/admin'

export async function submitApplication(formData: FormData) {
    // Use Admin Client to bypass RLS for public submissions
    const supabase = createAdminClient()

    // 1. Get Default Tenant (riyo-vize)
    const { data: tenant } = await supabase
        .from('tenants')
        .select('id')
        .eq('slug', 'riyo-vize')
        .single()

    const tenantId = tenant?.id

    if (!tenantId) {
        console.error("Submission Error: Default tenant not found")
        return { success: false, error: "System configuration error" }
    }

    // 2. Map Form Data to Lead Fields
    const rawData = {
        fullName: formData.get("fullName") as string,
        phone: formData.get("phone") as string,
        email: (formData.get("email") as string) || null,
        city: formData.get("city") as string,
        targetCountry: formData.get("targetCountry") as string,
        visaType: formData.get("visaType") as string,
        serviceType: (formData.get("serviceType") as string) || 'visa', // Default to visa
        profession: (formData.get("profession") as string) || null,
        notes: (formData.get("notes") as string) || null,
        kvkkApproved: formData.get("kvkk") === "on",
        age: formData.get("age") ? parseInt(formData.get("age") as string) : null,
        source: "website"
    }

    // Format extra details for initial_message or internal_notes
    const formattedNotes = `
Hizmet Türü: ${rawData.serviceType === 'denklik' ? 'Denklik Danışmanlığı' : 'Vize Danışmanlığı'}
Şehir: ${rawData.city}
Meslek: ${rawData.profession || '-'}
Notlar: ${rawData.notes || '-'}
    `.trim()

    const utmSource = formData.get("utm_source") as string
    const utmCampaign = formData.get("utm_campaign") as string

    const leadSource = (formData.get("lead_source") as string) ||
        (utmSource === 'meta' || utmSource === 'facebook' ? 'meta_ads' :
            (utmSource === 'instagram' ? 'instagram_ads' :
                (utmSource === 'google' ? 'google_ads' : 'website_form')));

    const leadData = {
        tenant_id: tenantId,
        full_name: rawData.fullName,
        phone: rawData.phone,
        email: rawData.email,
        lead_source: leadSource,
        lead_campaign: utmCampaign || (formData.get("lead_campaign") as string) || null,
        target_country: rawData.targetCountry,
        age: rawData.age,
        visa_type: rawData.serviceType === 'denklik' ? 'Denklik Danışmanlığı' : rawData.visaType,
        service_type: rawData.serviceType,
        profession: rawData.profession,
        kvkk_approved: rawData.kvkkApproved,
        kvkk_approved_at: rawData.kvkkApproved ? new Date().toISOString() : null,
        initial_message: formattedNotes,
        status: 'new',
        created_at: new Date().toISOString()
    }

    // 3. Insert into Leads
    const { data, error } = await supabase
        .from("leads")
        .insert(leadData)
        .select()
        .single()

    if (error) {
        console.error("Submission Error:", error)
        return { success: false, error: error.message }
    }

    // 4. Send Welcome Email (using Lead data)
    if (leadData.email) {
        await sendEmail({
            to: leadData.email,
            subject: 'Vize Başvurunuz Alındı - Riyo Vize',
            react: WelcomeEmail({
                applicantName: leadData.full_name,
                applicationId: data.id // Use Lead ID for reference
            })
        })
    }

    return { success: true, data }
}

type ManualEmailPayload =
    | { type: 'status'; newStageLabel: string; customMessage?: string }
    | { type: 'payment'; amount: number; currency: string; dueDate?: string }

export async function sendManualEmail(appId: string, payload: ManualEmailPayload) {
    const supabase = await createClient()

    // 1. Fetch Application & User
    const { data: app, error } = await supabase
        .from('applications')
        .select('applicant_email, applicant_full_name')
        .eq('id', appId)
        .single()

    if (error || !app) {
        return { success: false, error: 'Application not found' }
    }

    if (!app.applicant_email) {
        return { success: false, error: 'Applicant has no email address' }
    }

    // 2. Determine Template & Send
    let subject = ''
    let reactComponent = null

    if (payload.type === 'status') {
        subject = 'Başvuru Durumunuz Güncellendi - Riyo Vize'
        reactComponent = StatusUpdateEmail({
            applicantName: app.applicant_full_name,
            newStageLabel: payload.newStageLabel,
            customMessage: payload.customMessage
        })
    } else if (payload.type === 'payment') {
        subject = 'Ödeme Hatırlatması - Riyo Vize'
        reactComponent = PaymentReminderEmail({
            applicantName: app.applicant_full_name,
            amount: payload.amount,
            currency: payload.currency,
            dueDate: payload.dueDate
        })
    } else {
        return { success: false, error: 'Invalid email type' }
    }

    const result = await sendEmail({
        to: app.applicant_email,
        subject,
        react: reactComponent
    })

    return result
}
